import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

import type { Community } from '@/types/v2/community';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { CreateOrEditCommunityModal } from '@/components/communities/create-or-edit-community-modal';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';

export function CommunitySettingsPage(): React.JSX.Element {
  const { community, setCommunity } = useUser();
  const dialog = useDialog();
  const theme = useTheme();
  const handleSuccess = React.useCallback(
    (community: Community) => {
      setCommunity(community, false);
      toast.success('Community updated successfully', {
        style: {
          backgroundColor: theme.palette.success.main,
          color: 'white',
        },
        position: 'top-right',
        icon: '🎉',
      });
      dialog.handleClose();
    },
    [dialog, setCommunity]
  );

  return (
    <React.Fragment>
      <Stack spacing={4} sx={{ width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid lg={12} sx={{ width: '100%' }} xs={12}>
            <Card sx={{ width: '100%' }}>
              <CardHeader
                action={
                  <IconButton onClick={() => { dialog.handleOpen(); }}>
                    <PencilSimpleIcon />
                  </IconButton>
                }
                title="Community Details"
              />
              <CardContent>
                <PropertyList
                  divider={<Divider />}
                  orientation="vertical"
                  sx={{ '--PropertyItem-padding': '12px 24px' }}
                >
                  {(
                    [
                      { key: 'ID', value: <Chip label={community?.id} size="small" variant="soft" /> },
                      { key: 'Name', value: community?.name },
                      { key: 'Email', value: community?.email },
                      { key: 'Phone', value: community?.phone || 'N/A' },
                    ] satisfies { key: string; value: React.ReactNode }[]
                  ).map(
                    (item): React.JSX.Element => (
                      <PropertyItem key={item.key} name={item.key} value={item.value} />
                    )
                  )}
                </PropertyList>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
      <CreateOrEditCommunityModal
        communityId={String(community?.id)}
        initialData={community!}
        mode="edit"
        onClose={dialog.handleClose}
        onSuccess={handleSuccess}
        open={dialog.open}
      />
    </React.Fragment>
  );
}
